import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node'
import { redirect, useLoaderData } from '@remix-run/react'
import { useHydrateAtoms } from 'jotai/utils'
import { omit } from 'lodash-es'
import { aListingContact } from '~/atoms'
import {
  ListingTransaction,
  TokenUser,
  UserGroup,
  UserRole,
  type Listing,
} from '~/models'
import { activityHelper, findListing, findListingContact } from '~/mongo.server'
import { getRoutMeta, transformImageUrl } from '~/utils'
import { getOrganizationSlug, parseRequestUser } from '~/utils.server'
import ListingDetailLayout from './Layout'

type LoaderData = {
  listing: Listing
}

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const slug = params.slug

  if (!slug) {
    throw new Response('Listing Not found', { status: 404 })
  }
  const organizationSlug = getOrganizationSlug(request)

  const [listing, listingContact] = await Promise.all([
    findListing({
      slug,
      'organization.slug': organizationSlug,
    }),
    findListingContact(slug, organizationSlug, request),
  ])

  if (!listing && slug.length >= 8) {
    const tid = slug.slice(0, 8).toLowerCase()
    const updatedListing = await findListing({
      tid,
      'organization.slug': organizationSlug,
    })
    if (updatedListing) {
      return redirect(`/listings/${updatedListing.slug}`, { status: 302 })
    }
  }
  if (!listing) {
    throw new Response('Listing Not found', { status: 404 })
  }
  await activityHelper(request)
    .source('app')
    .type('view')
    .related('listing', listing)
    .write()

  const user = await parseRequestUser(request)

  const getCanEdit = (user: TokenUser | null) => {
    if (!user) {
      return false
    }
    const case1 = listing.brokers.some(
      (broker) => broker._id.toString() === user._id.toString()
    )
    const case2 =
      (user.role === UserRole.managingBroker ||
        user.role === UserRole.coordinator) &&
      listing.company._id.toString() === user?.company?._id.toString()
    return (
      user.group === UserGroup.superAdmin ||
      user.group === UserGroup.organizationAdmin ||
      case1 ||
      case2
    )
  }

  const canEdit = getCanEdit(user)

  if (!canEdit && listing.status === 'draft') {
    throw new Response('Listing Not found', { status: 404 })
  }

  if (!user) {
    listing.meta = omit(listing.meta, ['notes'])
    delete listing.transactions // could move to projection
  } else if (!canEdit && listing.transactions) {
    // remove confidential lease information. Keep footage, acres, date, brokers
    listing.transactions = listing.transactions!.map((transaction) => {
      if (transaction.confidential === false) {
        // If not confidential, return rate and price for member display
        return omit(transaction, [
          'lease.totalLeaseValue',
          'lease.rateType',
          'lease.structure',
          'lease.tenantName',
          'lease.startDate',
          'lease.termInMonths',
          'sale.terms',
        ]) as ListingTransaction
      }
      return omit(transaction, [
        'lease.rate',
        'lease.totalLeaseValue',
        'lease.rateType',
        'lease.structure',
        'lease.tenantName',
        'lease.startDate',
        'lease.termInMonths',
        'sale.price',
        'sale.terms',
      ]) as ListingTransaction
    })
  }

  // Remove sensitive information
  delete listing.expirationDate

  return {
    listing,
    canEdit,
    listingContact,
  }
}

export const meta: MetaFunction<typeof loader> = ({ matches, data }) => {
  const title = data?.listing.name || data?.listing.property.address || ''
  return getRoutMeta({
    matches,
    title,
    description: data?.listing.meta.description,
    image:
      (data?.listing.images || []).length > 0
        ? transformImageUrl(data!.listing.images![0].thumbUrl)
        : undefined,
  })
}

export default function AppListingDetailRoute() {
  const { listing, canEdit, listingContact } = useLoaderData<typeof loader>()
  useHydrateAtoms([[aListingContact, listingContact]])

  return <ListingDetailLayout canEdit={canEdit} listing={listing} />
}
